import { TrackingEvents } from "../../services/trackingEvents";
import type { TrackingEventName, TrackingEventProps } from "../../services/trackingEvents";
import trackingService from "../../services/trackingService";

const STORAGE_PREFIX = "ph_alpha_";

export type AlphaSetupEntry = "welcome" | "direct" | "auto_route";
export type AlphaConfigFlag = "isbeta" | "iscustomer";
export type AlphaFlagObserveResult = "fired" | "recorded" | "skipped";

const storageKey = (suffix: string, userId: string): string => (
  `${STORAGE_PREFIX}${suffix}:${userId}`
);

const readStorage = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Analytics must never break setup if storage is unavailable.
  }
};

export function trackAlphaEvent(
  isDevPreview: boolean,
  event: TrackingEventName,
  props: TrackingEventProps = {}
): void {
  if (isDevPreview) return;
  trackingService.track(event, props);
}

export function consumeAlphaOnceFlag(userId: string, flag: string): boolean {
  const key = storageKey(flag, userId);
  if (readStorage(key) === "true") return false;
  writeStorage(key, "true");
  return true;
}

export function observeAlphaConfigFlag(
  userId: string | null | undefined,
  flag: AlphaConfigFlag,
  current: boolean
): AlphaFlagObserveResult {
  if (!userId) return "skipped";

  const key = storageKey(`seen_${flag}`, userId);
  const stored = readStorage(key);
  if (stored === null && current) {
    // First observe of an already-true flag is not a launch. Record only.
    writeStorage(key, "true");
    return "recorded";
  }

  const shouldFire = current && stored === "false";
  writeStorage(key, current ? "true" : "false");
  return shouldFire ? "fired" : "recorded";
}

export function syncAlphaConfigAnalytics(params: {
  isDevPreview: boolean;
  userId: string | null | undefined;
  configReady: boolean;
  isbeta: boolean;
  iscustomer: boolean;
  isretirement: boolean;
  launchDate: string | null;
  isReviewComplete: boolean;
}): void {
  const { isDevPreview, userId, configReady } = params;
  if (isDevPreview || !userId || !configReady) return;

  trackingService.identify(userId, {
    isbeta: params.isbeta,
    iscustomer: params.iscustomer,
    isretirement: params.isretirement,
  });

  if (observeAlphaConfigFlag(userId, "isbeta", params.isbeta) === "fired") {
    trackAlphaEvent(false, TrackingEvents.ALPHA_BROKERAGE_PROVISIONED, {
      source: "config",
    });
  }
  if (observeAlphaConfigFlag(userId, "iscustomer", params.iscustomer) === "fired") {
    trackAlphaEvent(false, TrackingEvents.ALPHA_CUSTOMER_LAUNCHED, {
      launch_date: params.launchDate,
    });
  }

  if (
    params.isReviewComplete
    && (params.isbeta || params.iscustomer)
    && consumeAlphaOnceFlag(userId, "launched")
  ) {
    trackAlphaEvent(false, TrackingEvents.ALPHA_LAUNCHED, {
      launch_basis: params.isbeta && params.iscustomer
        ? "both"
        : params.iscustomer
          ? "iscustomer"
          : "isbeta",
      launch_date: params.launchDate,
      detection_method: "first_observed_true",
    });
  }
}

export function trackAlphaSetupViewedOnce(params: {
  isDevPreview: boolean;
  userId: string | null | undefined;
  configReady: boolean;
  entry: AlphaSetupEntry;
}): void {
  if (params.isDevPreview || !params.userId || !params.configReady) return;
  if (!consumeAlphaOnceFlag(params.userId, "setup_viewed")) return;
  trackAlphaEvent(false, TrackingEvents.ALPHA_SETUP_VIEWED, { entry: params.entry });
}

export function trackAlphaCertificationVerifiedOnce(params: {
  isDevPreview: boolean;
  userId: string | null | undefined;
}): void {
  if (params.isDevPreview || !params.userId) return;
  if (!consumeAlphaOnceFlag(params.userId, "certification_verified")) return;
  trackAlphaEvent(false, TrackingEvents.ALPHA_CERTIFICATION_VERIFIED, {
    source: "agreement_status",
  });
}

export function resetAlphaAnalyticsStorageForTests(): void {
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key && key.startsWith(STORAGE_PREFIX)) keysToRemove.push(key);
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Test helper; ignore storage failures.
  }
}
